/**
 * Opens the size chart <dialog> referenced by a trigger button's
 * `data-size-chart-open` attribute. Delegated at the document level since the
 * trigger (inside a <legend>, which only allows phrasing content) and the
 * dialog it opens (rendered elsewhere) aren't nested inside each other.
 */
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const trigger = target.closest('[data-size-chart-open]');
  if (!trigger) return;

  const id = trigger.getAttribute('data-size-chart-open');
  const dialog = id ? document.getElementById(id) : null;
  if (!(dialog instanceof HTMLDialogElement)) return;

  dialog.showModal();
  // Avoid the browser's default behavior of auto-focusing the first
  // focusable descendant (the CM/Inches toggle), which shows a jarring
  // focus ring on open. Focus the dialog itself instead.
  dialog.focus();
});

/**
 * Wraps the size chart <dialog>: closes on backdrop click / close button,
 * and toggles the table between cm and inches.
 */
class SizeChartDialog extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  get #dialog() {
    return this.querySelector('dialog');
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target === this.#dialog || target.closest('.size-chart__close')) {
      this.#dialog?.close();
      return;
    }

    const unitToggle = target.closest('.size-chart__unit-toggle');
    if (unitToggle instanceof HTMLElement) {
      this.#setUnit(unitToggle);
    }
  };

  /**
   * @param {HTMLElement} button
   */
  #setUnit(button) {
    const unit = button.dataset.unit;
    if (unit !== 'cm' && unit !== 'in') return;

    this.querySelectorAll('.size-chart__unit-toggle').forEach((toggle) => {
      toggle.setAttribute('aria-pressed', String(toggle === button));
    });

    this.querySelectorAll('td[data-cm]').forEach((cell) => {
      if (!(cell instanceof HTMLElement)) return;
      const value = unit === 'cm' ? cell.dataset.cm : cell.dataset.in;
      cell.textContent = value ?? '';
    });
  }
}

if (!customElements.get('size-chart-dialog')) {
  customElements.define('size-chart-dialog', SizeChartDialog);
}
